import React, { useCallback } from "react";
import { Link } from "react-router-dom";

import { faSpinnerThird } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/browser";
import cx from "classnames";

import { dispatchAnalyticsTrack, getReferringEntities } from "~/lib/segment";
import { isiOSApp, isZoom } from "~/lib/utils";
import type { AuthFlowType } from "~/types/main.d";
import { Button } from "~/ui";
import { ButtonLink, Modal } from "~/ui/legacyFellowIndex";
import { __globalModalContext } from "~/ui/modals";

import { AuthBase } from "../AuthBase";
import { WhitelabelPane } from "../panes";
import type { AuthProps } from "../types";
import { WorkspaceRow, workspaceSort } from "./utils";

import styles from "../css/Auth.module.css";

interface SignupJoinState {
	loading: boolean;
	errorMessage: string | undefined;
	isExpanded: boolean;
}

export type JoinWorkspaceResult = { success: true; signup: AuthFlowType } | { success: false; errorMessage?: string };

function CreateWorkspaceModal(props: { close: (val?: boolean) => void }) {
	const { close } = props;

	const createWorkspace = useCallback(() => {
		close(true);
	}, [close]);

	const closeModal = useCallback(() => {
		close();
	}, [close]);

	return (
		<Modal onClose={closeModal}>
			<Modal.Header>
				<div className={styles.confirmCreateWorkspaceModalTitle}>Your teammates are waiting for you…</div>
			</Modal.Header>
			<Modal.Body>
				<div>
					With Fellow, you’ll always have control over your recordings and notes so they can be as private or public as
					you need them to be. Don’t miss out on all the powerful collaboration features!
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" size="normal" onClick={createWorkspace}>
					Create new workspace
				</Button>
				<Button variant="primary" size="normal" onClick={closeModal}>
					Go back
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

export default class SignupJoin extends AuthBase<AuthProps, SignupJoinState> {
	state: SignupJoinState = { loading: false, errorMessage: undefined, isExpanded: false };

	documentTitle = "Join | Signup";

	getPaneComponent() {
		return WhitelabelPane;
	}

	getPaneProps() {
		return { bgStyle: "purple" as const };
	}

	pickWorkspace(accountID: string | number | null, analyticsTag: string) {
		return () => {
			this.setState({ loading: true });

			let data = new FormData();

			/* eslint-disable camelcase */
			const analyticsData = {
				account_id: accountID,
				feature: "Authentication",
				workspace_choice_type: analyticsTag,
				...getReferringEntities(),
			};
			/* eslint-enable camelcase */

			if (accountID) {
				data.append("account_id", String(accountID));
			}

			fetch("/auth/save/join/", {
				method: "POST",
				body: data,
				headers: {
					"x-client-session-id": window.CLIENT_SESSION_ID,
					"x-csrftoken": window.CSRF_TOKEN,
					"x-requested-with": "XMLHttpRequest",
				},
			})
				.then((res: Response) => {
					return res.json();
				})
				.then((data: JoinWorkspaceResult) => {
					dispatchAnalyticsTrack("Signup Join Step Completed", analyticsData);
					/* eslint-disable camelcase */
					dispatchAnalyticsTrack("Signed Up", {
						is_account_created: !accountID,
						...analyticsData,
					});
					/* eslint-enable camelcase */

					window.setTimeout(() => {
						if (data.success) {
							if (data.signup.redirect) {
								window.location.href = data.signup.redirect;
							} else if (data.signup.next) {
								this.context.setAuthContext(data.signup);
								this.props.history.push(data.signup.next, this.props.location.state);
							}
						} else if (data.errorMessage) {
							dispatchAnalyticsTrack("Signup Join Step Failed", analyticsData);
							this.setState({ loading: false, errorMessage: data.errorMessage });
						}
					}, 100);
				})
				.catch(err => {
					Sentry.captureException(err);
					dispatchAnalyticsTrack("Signup Join Step Failed", analyticsData);
					this.setState({ loading: false, errorMessage: `An error occurred: ${err}` });
				});
		};
	}

	confirmCreateWorkspaceModal() {
		return async () => {
			const shouldCreateWorkspace = await __globalModalContext?.open<boolean>(({ close }) => {
				return <CreateWorkspaceModal close={close} />;
			});

			if (shouldCreateWorkspace) {
				this.pickWorkspace(null, "new")();
			}
		};
	}

	expandWorkspaceList() {
		return () => {
			this.setState({ isExpanded: true });
		};
	}

	renderFooter() {
		const {
			location: { search },
		} = this.props.history;

		const location = {
			pathname: "/auth/signup/",
			search,
		};

		return (
			<div className={styles.signupJoinFooter}>
				<div>Not seeing your workspace? </div>
				<Link to={location}>Try a different email</Link>
			</div>
		);
	}

	renderStep() {
		const { subdomain } = window.INITIAL_STATE;
		const { loading } = this.state;
		const { authoritative_domain: authoritativeDomain } = this.context;

		const domainAccounts =
			this.context.domain_accounts
				?.sort(workspaceSort)
				.map(account => (
					<WorkspaceRow
						key={account.id}
						account={account}
						subdomain={account.subdomain}
						loading={loading}
						accountType="domain"
						pickWorkspace={this.pickWorkspace(account.id, "domain")}
					/>
				)) ?? [];

		const memberAccounts =
			this.context.member_accounts
				?.sort(workspaceSort)
				.map(account => (
					<WorkspaceRow
						key={account.id}
						account={account}
						subdomain={account.subdomain}
						loading={loading}
						accountType="member"
						pickWorkspace={this.pickWorkspace(account.id, "member")}
					/>
				)) ?? [];

		const guestAccounts =
			this.context.guest_accounts
				?.sort(workspaceSort)
				.map(account => (
					<WorkspaceRow
						key={account.id}
						account={account}
						subdomain={account.subdomain}
						loading={loading}
						accountType="guest"
						pickWorkspace={this.pickWorkspace(account.id, "guest")}
					/>
				)) ?? [];

		const allAccounts = [...memberAccounts, ...guestAccounts, ...domainAccounts];

		const initialNumVisibleAccounts = 5;
		const visibleAccounts = this.state.isExpanded ? allAccounts : allAccounts.slice(0, initialNumVisibleAccounts);
		const extraAccounts = allAccounts.length - initialNumVisibleAccounts;

		return (
			<div className={cx(styles.authFlex, { [styles.authDisabled]: loading })}>
				<div>
					<>
						<div className={styles.signupJoinTitle}>Your teammates are already using Fellow</div>
						<div className={styles.signupJoinSubtitle}>
							These workspaces are open to join, choose one to start working with your team:
						</div>
						<div className={styles.workspaceList}>
							{visibleAccounts}
							{!this.state.isExpanded && allAccounts.length > initialNumVisibleAccounts && (
								<div className={styles.workspaceListMore}>
									<div className={styles.workspaceListMoreText}>
										{extraAccounts} more workspace
										{extraAccounts > 1 ? "s" : ""}
									</div>
									<ButtonLink className={styles.showAllWorkspaces} onClick={this.expandWorkspaceList()}>
										Show all workspaces
									</ButtonLink>
								</div>
							)}
						</div>
					</>

					{!isiOSApp && !subdomain && !authoritativeDomain && !isZoom && (
						<>
							{loading ? (
								<FontAwesomeIcon spin size="2x" icon={faSpinnerThird} />
							) : (
								<div className={styles.createWorkspaceWrapper}>
									<div>Want to use Fellow with a different team?</div>
									<button
										disabled={loading}
										onClick={this.confirmCreateWorkspaceModal()}
										className={styles.createWorkspace}
									>
										Create a new workspace
									</button>
								</div>
							)}
						</>
					)}
				</div>

				{authoritativeDomain && domainAccounts.length == 0 && memberAccounts.length == 0 && (
					<h5>Sorry! Your work has disabled workspace creation for this email domain.</h5>
				)}
			</div>
		);
	}
}
